import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import {
  ActionButton,
  Badge,
  Button,
  Card,
  Content as CardContent,
  Header as CardHeader,
} from '@react-spectrum/s2'
import { useState } from 'react'
import {
  Building,
  FileText,
  Loader2,
  PoundSterling,
  Trash2,
  Users,
} from 'lucide-react'
import {
  getAdminDocuments,
  getAdminStats,
  getAdminSubscribers,
  removeDocument,
} from '@rpc/admin.rpc'
import { fullWidthStyles } from '@/styles/s2-macros'

export const Route = createFileRoute('/admin/')({
  loader: async () => {
    const [documents, stats, subscribers] = await Promise.all([
      getAdminDocuments(),
      getAdminStats(),
      getAdminSubscribers(),
    ])
    return { documents, stats, subscribers }
  },
  component: AdminDashboard,
})

function AdminDashboard() {
  const { documents, stats, subscribers } = Route.useLoaderData()
  const router = useRouter()
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return

    setIsDeleting(id)
    try {
      await removeDocument({ data: { id } })
      router.invalidate()
    } catch (e) {
      alert('Failed to delete document')
    } finally {
      setIsDeleting(null)
    }
  }

  const estimatedMRR =
    subscribers.reduce(
      (acc: number, sub: any) =>
        acc +
        (typeof sub.monthlyPricePence === 'number' ? sub.monthlyPricePence : 0),
      0,
    ) / 100

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium">Total users</h3>
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsers}</div>
            <Link
              to="/admin/users"
              search={{ query: undefined }}
              className="text-xs text-blue-600 hover:underline"
            >
              Manage users
            </Link>
          </CardContent>
        </Card>
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium">Organizations</h3>
              <Building className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalOrgs}</div>
            <Link
              to="/admin/organizations"
              className="text-xs text-blue-600 hover:underline"
            >
              Manage organizations
            </Link>
          </CardContent>
        </Card>
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium">Documents</h3>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDocuments}</div>
            <p className="text-xs text-muted-foreground">Active in index</p>
          </CardContent>
        </Card>
        <Card styles={fullWidthStyles}>
          <CardHeader>
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-sm font-medium">Estimated MRR</h3>
              <PoundSterling className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{estimatedMRR.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">
              {subscribers.length} active subscriptions
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
          Indexed documents
        </h2>
        <Link to="/admin/upload">
          <Button>Upload chatbot document</Button>
        </Link>
      </div>

      <Card styles={fullWidthStyles}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-900/70">
              <tr>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Title
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Category
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Version
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Status
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {documents.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-neutral-500 italic"
                  >
                    No documents indexed yet.
                  </td>
                </tr>
              )}
              {documents.map((doc: any) => {
                return (
                  <tr
                    key={doc.id}
                    className="group transition-colors hover:bg-neutral-50/70 dark:hover:bg-neutral-900/40"
                  >
                    <td className="px-6 py-4 font-medium text-neutral-900 dark:text-neutral-100">
                      {doc.title}
                    </td>
                    <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                      {doc.category || '-'}
                    </td>
                    <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                      {doc.version || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          doc.status === 'active'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400'
                        }`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {doc.revisionId ? (
                          <Link
                            to={
                              `/admin/documents/${doc.id}/revisions/${doc.revisionId}` as any
                            }
                          >
                            <Button
                              variant="secondary"
                              fillStyle="outline"
                              size="S"
                            >
                              Open workflow
                            </Button>
                          </Link>
                        ) : (
                          <Button
                            variant="secondary"
                            fillStyle="outline"
                            size="S"
                            isDisabled
                          >
                            No revision
                          </Button>
                        )}
                        <ActionButton
                          aria-label={`Delete ${doc.title}`}
                          isQuiet
                          onPress={() => handleDelete(doc.id)}
                          isDisabled={isDeleting === doc.id}
                        >
                          {isDeleting === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Subscribers Section */}
      <h2 className="text-3xl font-bold text-neutral-900 dark:text-neutral-100 mt-12">
        Active subscribers
      </h2>
      <Card styles={fullWidthStyles}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-900/70">
              <tr>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  User
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Email
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Plan
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300">
                  Renewing
                </th>
                <th className="px-6 py-4 font-semibold text-neutral-700 dark:text-neutral-300 text-right">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {subscribers.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-10 text-center text-neutral-500 italic"
                  >
                    No active subscribers found.
                  </td>
                </tr>
              )}
              {subscribers.map((sub: any) => (
                <tr
                  key={sub.id}
                  className="transition-colors hover:bg-neutral-50/70 dark:hover:bg-neutral-900/40"
                >
                  <td className="px-6 py-4 font-medium text-neutral-900 dark:text-neutral-100">
                    {sub.user.name}
                  </td>
                  <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                    {sub.user.email}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="neutral" fillStyle="outline">
                      {formatPriceIdLabel(sub.stripePriceId)}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-neutral-600 dark:text-neutral-400">
                    {sub.currentPeriodEnd
                      ? new Date(sub.currentPeriodEnd).toLocaleDateString()
                      : '-'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      {sub.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

function formatPriceIdLabel(priceId?: string | null) {
  if (!priceId) return 'UNKNOWN'
  return priceId.replace('User', '').toUpperCase()
}
